import ErrorPage from '@components/atoms/ErrorPage';
import SplashLoader from '@components/atoms/SplashLoader';
import { SettingsProvider } from '@components/settings';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';

import './App.css';
import { DeepLinkContextProvider } from './contexts/DeepLinkContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { PwaInstallerContextProvider } from './contexts/PwaInstaller';
import ToastifyContainer from './contexts/ToastifyContainer';
import { useCookieConsent } from './hooks/useCookieConsent';
import './libs/translation/i18n';
import MainRouter from './router/MainRouter';
import ThemeProvider from './theme';
import { isClerkAuthEnabled } from './utils/clerkAuth';

import 'react-toastify/dist/ReactToastify.css';

const ClerkAuthProvider = lazy(() => import('@pages/auth/ClerkAuthProvider'));
const queryClient = new QueryClient();

function App() {
  useCookieConsent();

  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <SettingsProvider
            defaultSettings={{
              themeMode: 'light', // 'light' | 'dark'
              themeDirection: 'ltr', //  'rtl' | 'ltr'
              themeContrast: 'default', // 'default' | 'bold'
              themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
              themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
              themeStretch: false,
            }}
          >
            <ThemeProvider>
              <DeepLinkContextProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <PwaInstallerContextProvider>
                    <NotificationProvider>
                      {isClerkAuthEnabled() ? (
                        <Suspense fallback={<SplashLoader />}>
                          <ClerkAuthProvider>
                            <MainRouter />
                          </ClerkAuthProvider>
                        </Suspense>
                      ) : (
                        <MainRouter />
                      )}
                    </NotificationProvider>
                  </PwaInstallerContextProvider>
                </LocalizationProvider>
              </DeepLinkContextProvider>
            </ThemeProvider>
          </SettingsProvider>
        </QueryClientProvider>
        <ToastifyContainer />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}

export default App;
